import { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ShieldAlert } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO =
  "https://customer-assets-v7afamib.emergentagent.net/job_edju-command/artifacts/tmtoc27k_edju-cat-type-logo-png.png";
const SCENE =
  "https://static.prod-images.emergentagent.com/jobs/bc84386f-1b7b-4921-a324-fcf4509e7717/images/c366a2ca1683727aea5ed18b73a64cac823894d30eb2c4999db6832652709437.jpeg";
const MASCOT = "/commando-mascot-v2.webp";

function messageFromDetail(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.message === "string") return detail.message;
  return fallback;
}

function fmtCountdown(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m > 0) return `${m} minute${m !== 1 ? "s" : ""} ${s} second${s !== 1 ? "s" : ""}`;
  return `${s} second${s !== 1 ? "s" : ""}`;
}

export default function Login() {
  const { login, currentUser, loading } = useUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lock, setLock] = useState(null); // { unlockAt: Date, secs: number }
  const timerRef = useRef(null);

  // Live countdown — recomputes from unlock time each tick and auto-unlocks at zero.
  // Keyed on the unlock timestamp only (not the whole `lock` object) so the interval
  // is not torn down and rebuilt on every 1s tick.
  const unlockMs = lock?.unlockAt?.getTime();
  useEffect(() => {
    if (!lock) return;
    const tick = () => {
      const secs = Math.max(0, Math.round((lock.unlockAt.getTime() - Date.now()) / 1000));
      if (secs <= 0) {
        setLock(null);
        setError("");
      } else {
        setLock((l) => (l ? { ...l, secs } : l));
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockMs]);

  if (!loading && currentUser) return <Navigate to="/" replace />;

  const locked = !!lock && lock.secs > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (locked) return;
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429 && detail?.unlock_at) {
        const unlockAt = new Date(detail.unlock_at);
        setLock({ unlockAt, secs: Math.max(0, detail.seconds_remaining ?? Math.round((unlockAt - Date.now()) / 1000)) });
        setError("");
      } else {
        setError(messageFromDetail(detail, "Unable to sign in. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand scene — hidden on small screens */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={SCENE} alt="The Edjuverse" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E5B]/95 via-[#0B1E5B]/25 to-transparent" />
        <img
          src={MASCOT}
          alt="Edju Cat in commando-style War Room uniform"
          className="pointer-events-none absolute bottom-0 right-4 h-[62%] object-contain object-bottom drop-shadow-[0_12px_30px_rgba(0,0,0,0.45)]"
        />
        <div className="absolute bottom-0 left-0 z-10 p-10">
          <h2 className="font-heading text-4xl font-bold leading-tight text-white">
            What will you<br />learn today?
          </h2>
          <p className="mt-3 max-w-sm text-sm text-[#D6DEF6]">
            War Room — the command deck for the Edju Cat learning adventure.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="relative flex w-full items-center justify-center bg-[#F4F6FB] px-4 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="rounded-2xl bg-[#14245F] px-5 py-3">
              <img src={LOGO} alt="Edju Cat" className="h-8 w-auto" />
            </div>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.22em] text-[#1D4ED8]">
              War Room
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="login-form"
            className="rounded-3xl border border-[#E4E9F3] bg-white p-7 shadow-[0_10px_40px_rgba(20,36,95,0.10)]"
          >
            <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-[#5A6B8C]">
              <Lock size={15} className="text-[#1D4ED8]" />
              <span>Sign in to continue</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-[#14245F]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@edjucat.com"
                  autoComplete="email"
                  required
                  disabled={locked}
                  className="border-[#E4E9F3] bg-[#F7F9FE] focus-visible:ring-[#1D4ED8]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold text-[#14245F]">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  disabled={locked}
                  className="border-[#E4E9F3] bg-[#F7F9FE] focus-visible:ring-[#1D4ED8]"
                />
              </div>
            </div>

            {locked ? (
              <div
                data-testid="login-lockout"
                className="mt-4 rounded-xl border border-[#DC2626]/25 bg-[#DC2626]/8 px-4 py-3 text-sm text-[#B91C1C]"
              >
                <p className="flex items-center gap-1.5 font-bold">
                  <ShieldAlert size={15} /> Too many failed attempts.
                </p>
                <p className="mt-1" data-testid="login-countdown">
                  Try again in {fmtCountdown(lock.secs)}.
                </p>
                <p className="mt-0.5 text-xs text-[#C2410C]">
                  Unlocks at {lock.unlockAt.toLocaleTimeString()}
                </p>
              </div>
            ) : (
              error && (
                <p
                  data-testid="login-error"
                  className="mt-4 rounded-xl border border-[#FB6F5A]/30 bg-[#FB6F5A]/10 px-3 py-2 text-sm font-medium text-[#C2410C]"
                >
                  {error}
                </p>
              )
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting || locked}
              className="mt-6 w-full rounded-full bg-[#1D4ED8] py-5 font-bold text-white hover:bg-[#1A45C0] disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : locked ? "Locked" : "Sign In"}
            </Button>

            <div className="mt-4 text-center">
              <a
                href="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs font-semibold text-[#1D4ED8] transition-colors hover:text-[#14245F]"
              >
                Forgot your password?
              </a>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
